import { Injectable } from '@angular/core';
import { combineLatest, Observable, of, Subject, timer } from 'rxjs';
import { distinctUntilChanged, filter, map, share, switchMap } from 'rxjs/operators';

import { MarketplaceKeys } from '@config/keys/shop.keys';
import { DoodCartModel } from '@core/models/cart.model';
import { isSameCartContent } from '@shared/utils/cart/cart-equality.util';

import { CartStoreSelector } from '@common/selectors/cart.selector';
import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';
import { ShopStoreSelector } from '@common/selectors/shop.selector';
import { OrdersService } from '@core/services/orders/orders.service';

import { MarketplaceCartScopeValues } from '@config/values/marketplace.values';
import { SuggestionsSetsApiService } from '@core/services/api/suggestions-sets/suggestions-sets-api.service';
import {
  EligiblePromotionsLabelsService,
  mergePromotionLabelsIntoProducts,
} from '@core/services/eligible-promotions-labels/eligible-promotions-labels.service';
import {
  applyProductLabelsVisibility,
  isPromotionLabel,
} from '@shared/utils/product-labels/product-labels.util';
import { query } from '@shared/utils/rxjs/query';
import { toInject } from '@shared/utils/signal/to-inject';

/**
 * Custom throttle operator that bypasses throttling when cart changes from null to non-null
 * - By default: applies throttling with leading and trailing behavior
 * - If cart changes from null to non-null: bypasses throttle and executes immediately
 * - All other cases: maintains throttling with leading and trailing
 */
function smartThrottleTime<T>(throttleMs: number) {
  return (source: Observable<T>) => {
    let previousCart: any = null;
    let isFirstEmission = true;
    let isThrottling = false;
    let lastValue: T | null = null;
    let throttleTimer: Observable<T> | null = null;

    return source.pipe(
      share(),
      switchMap(value => {
        const cart = Array.isArray(value) ? value[0] : value;
        const currentCart = cart;

        // Detect transition from null to non-null (only bypass case)
        const isNullToNonNull =
          !isFirstEmission &&
          previousCart === null &&
          currentCart !== null &&
          currentCart !== undefined;

        // Update state
        previousCart = currentCart;
        isFirstEmission = false;

        // Only bypass throttling for null to non-null transition
        if (isNullToNonNull) {
          isThrottling = false;
          throttleTimer = null;
          return of(value);
        }

        // Apply throttling with leading and trailing behavior
        if (!isThrottling) {
          // Leading: execute immediately
          isThrottling = true;
          lastValue = value;

          // Set up trailing timer
          throttleTimer = timer(throttleMs).pipe(
            map(() => {
              isThrottling = false;
              const trailingValue = lastValue;
              lastValue = null;
              return trailingValue!;
            }),
          );

          return of(value);
        } else {
          // Store the latest value for trailing
          lastValue = value;
          return throttleTimer!;
        }
      }),
    );
  };
}

@Injectable({
  providedIn: 'root',
})
export class SuggestionsSetsService {
  constructor(
    private cartSelector: CartStoreSelector,
    private shopSelector: ShopStoreSelector,
    private readonly ordersService: OrdersService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly suggestionsSetsApi: SuggestionsSetsApiService,
    private readonly eligiblePromotionsLabelsService: EligiblePromotionsLabelsService,
  ) {}

  public readonly suggestionSetIdEntry = new Subject<string | undefined>();

  private readonly suggestionSetId$ = this.suggestionSetIdEntry.pipe(
    filter((suggestionsSetId): suggestionsSetId is string => !!suggestionsSetId),
  );

  public readonly productsToSuggestBySetQuery = query({
    params$: this.suggestionSetId$,
    identifier: suggestionsSetId => suggestionsSetId,
    loader: ({ params: suggestionsSetId }) => {
      return combineLatest([
        this.cartSelector.selectActive$.pipe(distinctUntilChanged(isSameCartContent)),
        this.shopSelector.selectActive$.pipe(distinctUntilChanged((a, b) => a?.id === b?.id)),
      ]).pipe(
        filter(([_cart, shop]) => {
          const marketplace = this.marketplaceSelector.marketplace;
          return (
            marketplace[MarketplaceKeys.CartScope] === MarketplaceCartScopeValues.Marketplace ||
            !!shop
          );
        }),
        smartThrottleTime(2500),
        switchMap(([cart, shop]) => {
          const marketplace = this.marketplaceSelector.marketplace;
          const order = this.ordersService.mapCartToOrderValues(cart as DoodCartModel, []);
          const context = {
            order,
            marketplace: marketplace[MarketplaceKeys.Id],
            ...(shop?.id ? { shop: shop.id } : {}),
          };
          return combineLatest([
            this.suggestionsSetsApi.getSuggestionsSet$(suggestionsSetId, context),
            this.eligiblePromotionsLabelsService.getPromotionLabelsByProductId$(),
          ]).pipe(
            map(([suggestions, promotionLabelsMap]) => {
              const products = suggestions.map(s => s.data);
              const withPromo = mergePromotionLabelsIntoProducts(products, promotionLabelsMap);
              return applyProductLabelsVisibility(withPromo, isPromotionLabel);
            }),
          );
        }),
      );
    },
  });
}

export const injectSuggestionsSetsService = toInject(SuggestionsSetsService);
